import { useEffect } from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';

import { DevTool } from '@hookform/devtools';
import { useResizeObserver } from '@mantine/hooks';
import classNames from 'classnames';

import { AnyChatForm, AnyChatFormProps } from '@anyx/feature-crm/shared';


import { LineMessageFormHeader, LineMessageFormHeaderProps } from './LineMessageFormHeader';
import { LineMessageProvider, LineMessageFormStoreProps, useLineMessageStore } from './store';

export type LineMessageFormProps<
  TFieldValues extends FieldValues,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TContext = any,
> = LineMessageFormHeaderProps &
  LineMessageFormStoreProps & {
    className?: string;
    slotBeforeShell?: React.ReactNode;
    methods: UseFormReturn<TFieldValues, TContext>;
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Root = <TFieldValues extends FieldValues, TContext = any>({
  title,
  headerAside,
  onLeavePage,
  hideBackBtn,
  className,
  methods,
  children,
  slotBeforeShell,
  ...storeProps
}: React.PropsWithChildren<
  LineMessageFormProps<TFieldValues, TContext> & Record<string, unknown>
>) => {
  return (
    <LineMessageProvider {...storeProps}>
      <FormProvider {...methods}>
        <AnyChatForm.Root className={classNames('max-w-7xl', className)}>
          <LineMessageFormHeader
            title={title}
            headerAside={headerAside}
            onLeavePage={onLeavePage}
            hideBackBtn={hideBackBtn}
          />
          {slotBeforeShell}
          <AnyChatForm.Shell className="relative h-full">
            {children}
            <DevTool control={methods.control} />
          </AnyChatForm.Shell>
        </AnyChatForm.Root>
      </FormProvider>
    </LineMessageProvider>
  );
};

export const Form = ({
  children,
  ...rest
}: React.PropsWithChildren<Omit<AnyChatFormProps, 'ref'>>) => {
  const name = useLineMessageStore((store) => store.view.name);
  const formHeight = useLineMessageStore((store) => store.formHeight);
  const setFormHeight = useLineMessageStore((store) => store.setFormHeight);

  const [ref, rect] = useResizeObserver<HTMLFormElement>();

  useEffect(() => {
    if (name === 'DEFAULT') {
      setFormHeight(rect.height || 'auto');
    }
  }, [name, setFormHeight, rect.height]);

  return (
    <AnyChatForm.Form
      noValidate
      ref={ref as React.RefObject<HTMLFormElement>}
      className="overflow-y-auto"
      style={{
        height: name === 'DEFAULT' ? 'auto' : formHeight, // block view should inherent the height from default view
      }}
      {...rest}
    >
      {children}
    </AnyChatForm.Form>
  );
};

export const ButtonGroup = AnyChatForm?.ButtonGroup;
export const Header = LineMessageFormHeader;
